import { TransportBase, type Transport } from './types.ts';

// Web Serial covers two physically different adapters with one API:
// USB adapters (OBDLink SX, EX) and Bluetooth Classic adapters paired at the
// OS level (OBDLink MX). The Bluetooth half needs Chrome 117+ on desktop and
// the SPP service class passed explicitly - without it the picker shows no
// Bluetooth devices at all and the failure looks like a hardware problem.

const SPP_SERVICE_CLASS_ID = '00001101-0000-1000-8000-00805f9b34fb';

export interface SerialTransportOptions {
  baudRate?: number;
  /** Reuse a port already granted in a previous session, skipping the picker. */
  port?: SerialPort;
}

export class SerialTransport extends TransportBase implements Transport {
  readonly kind = 'serial' as const;
  readonly label = 'USB / Bluetooth serial';

  private port: SerialPort | null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private baudRate: number;
  private readLoop: Promise<void> | null = null;

  constructor(opts: SerialTransportOptions = {}) {
    super();
    this.port = opts.port ?? null;
    this.baudRate = opts.baudRate ?? 115200;
  }

  get isOpen() {
    return Boolean(this.writer) && this.status.state === 'open';
  }

  /** Ports the user already granted. Lets a return visit reconnect without a prompt. */
  static async grantedPorts(): Promise<SerialPort[]> {
    if (!('serial' in navigator)) return [];
    try {
      return await navigator.serial.getPorts();
    } catch {
      return [];
    }
  }

  static async requestPort(): Promise<SerialPort> {
    if (!('serial' in navigator)) {
      throw new Error('Web Serial is unavailable. Use Chrome or Edge on desktop.');
    }
    // Must be called from a user gesture. `allowedBluetoothServiceClassIds`
    // is what surfaces paired Bluetooth Classic adapters in the picker.
    return navigator.serial.requestPort({
      allowedBluetoothServiceClassIds: [SPP_SERVICE_CLASS_ID],
    });
  }

  async open() {
    this.emitStatus('connecting', 'Opening serial port');
    if (!this.port) this.port = await SerialTransport.requestPort();

    await this.port.open({ baudRate: this.baudRate });

    if (!this.port.readable || !this.port.writable) {
      throw new Error('Serial port opened without usable streams.');
    }

    this.writer = this.port.writable.getWriter();
    this.reader = this.port.readable.getReader();
    this.emitStatus('open', `Serial at ${this.baudRate} baud`);

    this.readLoop = (async () => {
      const reader = this.reader!;
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) this.emitData(value);
        }
      } catch (err) {
        if (this.status.state === 'open') {
          this.emitStatus('error', `Serial read failed: ${(err as Error).message}`);
        }
      }
    })();
  }

  async close() {
    this.emitStatus('closing');
    try {
      await this.reader?.cancel();
    } catch {
      // stream already torn down
    }
    try {
      this.reader?.releaseLock();
      this.writer?.releaseLock();
    } catch {
      // locks already released
    }
    await this.readLoop?.catch(() => {});
    this.reader = null;
    this.writer = null;
    this.readLoop = null;
    try {
      await this.port?.close();
    } catch {
      // port already closed
    }
    this.emitStatus('closed');
  }

  async write(bytes: Uint8Array) {
    if (!this.writer) throw new Error('Serial transport is not open.');
    await this.writer.write(bytes);
  }
}
